import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  selectFileUploading,
  selectIntegration,
  selectRecordingDuration,
  selectRecordingStarted,
  selectReportId,
  setFileUploading,
  setIntegration,
  setRecordingDuration,
  setRecordingStarted,
  setReportId,
  setSelectedReport
} from '@/store/slices/ReportsSlice'
import { useTranslation } from 'react-i18next';
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'
import { useSubscription } from '@/shared/hooks/useSubscription'
import { formatTime } from '@/shared/lib/formatDuration'
import { emptyDocumentInfo } from '@/features/generation/generateReport/documentInfo'
import { runGeneratePipeline } from '@/features/generation/generateReport/runGeneratePipeline'
import { handleUpdateError } from '@/api/imperative/records'
export const useGenerateReport = (setHidingtabs, toolId) => {
  const { t } = useTranslation();
  const { toolName } = useCurrentTool();
  const { trialEnded } = useSubscription();
  const payload = useSelector((state) => state?.reportsSlice?.latestPayload);
  const uploading = useSelector(selectFileUploading);
  const recordingStarted = useSelector(selectRecordingStarted);
  const recordingDuration = useSelector(selectRecordingDuration);
  const newReportId = useSelector(selectReportId);
  const integration = useSelector(selectIntegration);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reportIdRef = useRef(null);

  const [inputStatus, setInputStatus] = useState('start');
  const [generateReport, setGenerateReport] = useState('');
  const [tempReport, setTempReport] = useState({});
  const [generating, setGenerating] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [finalising, setFinalising] = useState(false);

  const [documentInfo, setDocumentInfo] = useState(emptyDocumentInfo);

  const handleResetState = useCallback(() => {
    setInputStatus('start');
    setRecordingTime(0);
    setGenerateReport('');
    setTempReport({});
    setGenerating(false);
    setSelectedFile(null);
    setRecordedBlob(null);
    setAudioDuration(0);
    setHidingtabs(false);
    dispatch(setFileUploading(false));
    reportIdRef.current = null;
    setDocumentInfo(emptyDocumentInfo());
  }, [dispatch, setHidingtabs]);

  const retrySubmit = useCallback(() => {
    setGenerateReport('');
    setInputStatus('stopped');
    setGenerating(false);
    dispatch(setFileUploading(false));
    toast.error(t("form.errors.networkError"));
  }, [dispatch, t]);

  const clearGenerateStates = useCallback(() => {
    setDocumentInfo((prev) => ({
      ...emptyDocumentInfo(),
      documentId: prev.documentId,
    }));
  }, []);

  const handleGoToReport = useCallback(() => {
    const reportId = tempReport?.id || reportIdRef.current || documentInfo.documentId;
    const toolNameWord = toolName?.split(' ')[0]?.toLowerCase();
    if (!reportId || !toolNameWord) return;
    navigate(`/${toolNameWord}/${reportId}`);
  }, [navigate, tempReport?.id, documentInfo.documentId, toolName]);

  useEffect(() => {
    if (newReportId) {
      reportIdRef.current = newReportId;
    }
  }, [newReportId]);

  useEffect(() => {
    if (payload?.id === reportIdRef.current) {
      if (payload.report_fetched) {
        setTempReport(payload);
      } else if (payload.status === "completed") {
        handleResetState();
      }
    }
    if (tempReport?.report?.length > 0 && generateReport === 'generating' && generating && !uploading) {
      setGenerating(false);
      setGenerateReport('completed');
      dispatch(setSelectedReport(tempReport));
    }
  }, [payload, handleResetState, tempReport, dispatch, generateReport, generating, uploading]);

  const handleGenerateReport = async (data) => {
    if (trialEnded) {
      toast.error(t('subscription.trialEndedMessage'));
      return;
    }

    try {
      await runGeneratePipeline({
        data,
        toolId,
        toolName,
        integration,
        newReportId,
        recordingStarted,
        recordingDuration,
        recordedBlob,
        selectedFile,
        documentInfo,
        setDocumentInfo,
        dispatch,
        setRecordingStarted,
        setRecordingDuration,
        setReportId,
        setFileUploading,
        setIntegration,
        setGenerating,
        retrySubmit,
        clearGenerateStates,
        handleResetState,
        reportIdRef,
      });
    } catch (error) {
      console.log("API error ", error);
      if (JSON.stringify(error) === '{}') {
        handleUpdateError({ id: documentInfo.documentId, error: 'Failed to make request to backend server' });
      } else {
        handleUpdateError({ id: documentInfo.documentId, error: JSON.stringify(error) });
      }
      clearGenerateStates();
      handleResetState();
    }
  };

  return {
    inputStatus,
    setInputStatus,
    generateReport,
    setGenerateReport,
    generating,
    uploading,
    recordingTime,
    setRecordingTime,
    recordedBlob,
    setRecordedBlob,
    selectedFile,
    setSelectedFile,
    audioDuration,
    setAudioDuration,
    documentInfo,
    finalising,
    setFinalising,
    
    handleResetState,
    handleGenerateReport,
    handleGoToReport,
    formatTime,
    retrySubmit,
    
    reportIdRef,
    tempReport
  };
};
